import React from 'react';
import { Link } from 'react-router-dom';
import DocsDiagram from '../components/DocsDiagram';
import {
  bigPicture,
  commandBus,
  uploads,
  fanout,
  voicePaths,
  remoteAccess,
} from '../data/docsDiagrams';

// The in-app owner's manual: what every part of the app does and how the
// system hangs together, with live-rendered architecture diagrams. Deeper
// operational detail lives in the repo (docs/) and the project brain; this
// page is the household-facing book of record.

const SECTIONS = [
  ['overview', 'The big picture'],
  ['planner', 'Renovation planner'],
  ['uploads', 'Uploads & guest drops'],
  ['jobs', 'Processing & jobs'],
  ['controls', 'House controls'],
  ['voice', 'Voice assistant'],
  ['remote', 'House remote'],
  ['media', 'Media & cameras'],
  ['network', 'Network & remote access'],
  ['security', 'Security model'],
  ['operations', 'Operations'],
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="ah-docs-section">
      <h2 className="ah-ctl-section">{title}</h2>
      {children}
    </section>
  );
}

export default function Docs() {
  return (
    <div className="cs-page ah-docs">
      <header className="ah-docs-head">
        <h1>How this all works</h1>
        <p className="cs-help">
          The owner&apos;s manual for amalfi.house — every feature, every moving part,
          and the diagrams that connect them. The live network inventory (which device
          is on which switch port and SSID) stays on{' '}
          <Link to="/controls">Controls → Network</Link>.
        </p>
      </header>

      <nav className="ah-docs-toc">
        {SECTIONS.map(([id, title]) => (
          <a key={id} href={`#${id}`}>{title}</a>
        ))}
      </nav>

      <Section id="overview" title="The big picture">
        <p>
          One app, two jobs: the <strong>renovation planner</strong> for Amalfi House
          (spaces, moodboards, files, room renders) and the <strong>command center </strong>
          for the house itself (lights, pool &amp; spa, AC, shades, cameras, TV, voice).
          Under the hood it&apos;s a React app that talks straight to Firestore — there is
          no traditional REST API for data. The page you&apos;re reading, the device tiles,
          the job monitors: all of them are live Firestore subscriptions, which is why
          everything updates in place without refreshing.
        </p>
        <p>
          Firestore plays three roles at once: the <em>database</em>, the <em>job queue </em>
          (background work is a doc with a status flag that a worker claims), and the{' '}
          <em>command bus</em> (device commands are docs that bridges execute). A Node.js
          backend on AWS Fargate handles the pieces a browser can&apos;t — presigning
          uploads, running the voice agent, background workers — and two bridge tiers
          translate Firestore docs into real-world device protocols: one in AWS (Insteon,
          pool/spa) and one on a Raspberry Pi at the house (Home Assistant, Bluetooth
          lamps, AC, shades, cameras, local speech).
        </p>
        <DocsDiagram spec={bigPicture} />
      </Section>

      <Section id="planner" title="Renovation planner">
        <p>
          The public-facing half. Anyone can browse; editing requires an allowlisted
          sign-in (enforced server-side by Firestore rules, not just the UI).
        </p>
        <ul>
          <li>
            <strong><Link to="/">The House</Link></strong> — every renovation space
            (bedrooms, bathrooms, powder room, pool, sauna, yards, exterior, garage).
            Each space page carries a <em>moodboard</em> (inspiration images with notes),{' '}
            <em>lists</em> (tasks, fixtures, decisions), and <em>files</em> (quotes,
            plans, photos — anything uploaded lands here).
          </li>
          <li>
            <strong><Link to="/rooms">Rooms</Link></strong> — the as-built record:
            renders, blueprints, dimensions and stud maps per room, used when a
            contractor asks &quot;what&apos;s behind that wall?&quot;
          </li>
          <li>
            <strong><Link to="/designs">Designs</Link></strong> — the curated design
            plans, public-read by design so they can be shared with vendors by URL.
          </li>
        </ul>
      </Section>

      <Section id="uploads" title="Uploads & guest drops">
        <p>
          Files upload from the browser <strong>straight to S3</strong> — the backend
          only signs the requests, so multi-gigabyte videos never squeeze through a
          server. Parts upload in parallel with retry, and an interrupted upload resumes
          after a page reload.
        </p>
        <p>
          <strong>Guest drop links</strong> let anyone contribute without an account:
          generate a link (optionally passcode-protected), text it to a contractor, and
          their upload streams into the right space while you watch the progress bar
          live in the app. Every completed upload records provenance (who, from where)
          and is verified by a background worker before it counts.
        </p>
        <DocsDiagram spec={uploads} />
      </Section>

      <Section id="jobs" title="Processing & jobs">
        <p>
          Heavy work runs as <strong>fan-out jobs</strong>: a single Firestore doc
          describes the job, an orchestrator splits it into a prep task, N parallel
          chunk tasks on short-lived Fargate containers, and a stitch task that merges
          the results. The <strong><Link to="/jobs">Processes</Link></strong> page is a
          live window into all of it — per-task progress, and cancel/pause/resume
          buttons that every long-running job honors mid-flight.
        </p>
        <p>
          Smaller background work (verifying uploads, indexing knowledge for the voice
          assistant, refreshing media rails) runs as always-on workers that claim work
          transactionally — so even during a deploy, when two copies briefly overlap,
          nothing gets processed twice.
        </p>
        <DocsDiagram spec={fanout} />
      </Section>

      <Section id="controls" title="House controls">
        <p>
          Every device follows one pattern: the app (or the voice assistant, or an
          automation) writes a <strong>command doc</strong>, the bridge responsible for
          that device family executes it against the real hardware, and publishes the
          device&apos;s live state back — which flows into the tiles you see. Four
          bridges cover the house:
        </p>
        <ul>
          <li><strong>ISY bridge</strong> (AWS) — Insteon wall switches, dimmers, scenes and ISY automation programs. Wall-switch presses mirror back into the app within a second.</li>
          <li><strong>Aqualink bridge</strong> (AWS) — pool &amp; spa pumps, heaters, lights and setpoints via the Zodiac cloud.</li>
          <li><strong>HA bridge</strong> (Pi) — Home Assistant devices: the Dyson lamps (over Bluetooth through ESP32 proxies), the bedroom Gree AC, the bedroom Dyson purifier fan (speed, auto mode, night mode, oscillation, sleep timer — plus its live air-quality and filter readings), and the Apple TV.</li>
          <li><strong>Shade bridge</strong> (Pi) — the motorized Rollease shades, with automatic retry for the hub&apos;s weak Wi-Fi.</li>
        </ul>
        <p>
          App-side <strong>automations</strong> ride the same bus: dimmer auto-heal
          (snaps guest-mangled dimmers back to their configured level), auto-off rules
          for glitchy switches, the pool/spa temperature sampler, and the 5&nbsp;am spa
          catch-off. All of their knobs — and every other tunable in the system — live
          in a <strong>live config</strong> doc that applies within a minute, no deploy.
        </p>
        <DocsDiagram spec={commandBus} />
      </Section>

      <Section id="voice" title="Voice assistant">
        <p>
          One brain, three doors. The floating assistant in the app does full
          two-way voice (it speaks and interrupts naturally) via a realtime model;
          the same panel accepts typed chat; and the physical house remote routes
          through local speech-to-text on the Pi. All three converge on the same
          backend agent with the same tools: device control, scenes, house knowledge,
          media search, and app navigation.
        </p>
        <p>
          The assistant is <strong>interpretive by design</strong> — it receives each
          device&apos;s live state with every request, so loose phrasing works:
          &quot;child mode&quot; resolves to the Chill scene, &quot;turn off the patio
          strings&quot; hits all three string-light switches, &quot;dim the kitchen a
          couple clicks&quot; does relative dimming. House knowledge (documents in the
          knowledge base, indexed with vector search) backs questions like &quot;where
          is the AC filter?&quot;
        </p>
        <DocsDiagram spec={voicePaths} />
      </Section>

      <Section id="remote" title="House remote">
        <p>
          The pocket M5StickS3 is a push-to-talk remote for the whole house: tap the
          front button, speak, stop talking — it transcribes locally (audio never
          leaves the LAN), sends the words to the assistant, executes, and answers
          through its speaker. Front hold = all lamps off; side button toggles the
          Morph lamp. Actions land in about 5 seconds; commands dispatch immediately
          rather than waiting for confirmation, so on a rare miss just ask again.
          Keep it on USB-C — the battery is tiny — and don&apos;t hold the front button
          while plugging in (that&apos;s the firmware-flash chord; unplug/replug to
          recover). The full usage guide lives on{' '}
          <Link to="/controls">Controls → Network</Link>.
        </p>
      </Section>

      <Section id="media" title="Media & cameras">
        <ul>
          <li>
            <strong>TV</strong> — the living-room Apple TV is a device like any other:
            on/off and volume ride HDMI-CEC, and the Media tab shows browse rails
            (latest uploads from subscribed YouTube channels, top picks per streaming
            app) — tap a card and it plays on the TV. The assistant can search
            YouTube/Netflix by voice and either play the top hit or show choices.
          </li>
          <li>
            <strong>House DJ</strong> — the owner&apos;s AI DJ deck app streams one
            continuous mix into an Icecast relay on the house Pi, and the six Sonos
            zones tune in as a synced group. The Media tab&apos;s DJ panel picks
            rooms (or the whole house) and sets per-room volume; the voice
            assistant does the same (&quot;play the DJ in the lounge&quot;,
            &quot;stop the music everywhere&quot;). Now-playing titles flow from
            the stream&apos;s metadata automatically. The DJ card draws the
            deck&apos;s own waveform pack (interpolated, delayed to match the
            speakers) only while a Sonos zone is verifiably playing the stream.
            A USB house mic on the Pi is published as Icecast <code>/mic</code>
            for the DJ app to pull — it is not mixed into the house stream here.
          </li>
          <li>
            <strong>Cameras</strong> — Ring cameras mirror motion, battery and
            snapshots into the app (read-only), with on-demand live view streamed
            through the Pi. The assistant answers &quot;any motion out front?&quot;
            from the same data.
          </li>
        </ul>
      </Section>

      <Section id="network" title="Network & remote access">
        <p>
          The house LAN&apos;s book of record — every device, switch port, SSID
          assignment and live status dot — is the{' '}
          <Link to="/controls">Controls → Network</Link> tab, drawn from the same
          Firestore inventory the diagrams use. Two Wi-Fi networks matter: the main
          SSID for people, and the IoT SSID (no PMF, for compatibility) where the
          smart devices live.
        </p>
        <p>
          <strong>Remote access is zero-trust:</strong> nothing on the router is
          port-forwarded for administration. Instead, the Pi is a Tailscale subnet
          router — your devices join a private WireGuard mesh keyed to device
          identity, and through it you reach the router admin UI, Home Assistant, and
          live cameras from anywhere, as if you were home. The only public surfaces
          are the app and its API.
        </p>
        <DocsDiagram spec={remoteAccess} />
      </Section>

      <Section id="security" title="Security model">
        <ul>
          <li><strong>Firestore rules are the boundary</strong> — the app ships no secrets; reads are public where intended (design plans), writes require an allowlisted account, and controls require the controls allowlist. The same rules bind everyone, UI or not.</li>
          <li><strong>House control is owner-gated twice</strong> — the voice agent&apos;s control tools are stripped from the registry for non-owners (the model never even sees them), and every command doc records who requested it.</li>
          <li><strong>Secrets never touch the browser or git</strong> — the backend loads them at runtime from AWS SSM/Secrets Manager.</li>
          <li><strong>Voice privacy</strong> — the house remote&apos;s audio is transcribed on the Pi; only text leaves the LAN. In-app voice uses the provider&apos;s realtime API with short-lived session tokens.</li>
          <li><strong>Guest drops are scoped</strong> — token + optional passcode, upload-only, with provenance recorded.</li>
        </ul>
      </Section>

      <Section id="operations" title="Operations">
        <ul>
          <li><strong>Tunables change live</strong> — parallelism, sampling intervals, automation rules and voice settings live in the config doc and apply in under a minute. If changing a number requires a deploy, it&apos;s in the wrong place.</li>
          <li><strong>Deploys are zero-downtime</strong> — the new backend must pass health checks before the old one drains; code-only changes ship in about a minute via an overlay, full image rebuilds in 5–8.</li>
          <li><strong>Everything is observable in-app</strong> — bridge heartbeats power the live dots on the Network tab, Processes shows every job, and voice interactions are logged for after-the-fact audits.</li>
          <li><strong>The project brain</strong> — a wiki in the repo records every architectural decision, lesson and runbook, so the system&apos;s hard-won knowledge compounds instead of evaporating.</li>
        </ul>
      </Section>
    </div>
  );
}
